import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import * as t from "@babel/types";
import { isGeneralCallExpression, isGeneralMemberExpression, validateEmbeddedExpression, validateGlobalApi } from "./validations.js";
import { parseJSXElement } from "./parseJSXElement.js";
import { parsePropValue } from "./parseJsValue.js";
import { parseChildren } from "./parseChildren.js";
export function parseElement(path, state, app, options) {
  if (path.isJSXFragment()) {
    var children = path.get("children");
    return children.flatMap(function (child) {
      return parseElement(child, state, app, options);
    });
  }
  if (path.isJSXText()) {
    if (path.node.value.trim()) {
      return {
        type: "text",
        text: path.node.value
      };
    }
    return null;
  }
  if (path.isJSXExpressionContainer()) {
    return parseElement(path.get("expression"), state, app, options);
  }
  if (path.isJSXEmptyExpression()) {
    return null;
  }
  if (path.isJSXElement()) {
    return parseJSXElement(path, state, app, options);
  }
  if (isGeneralCallExpression(path)) {
    var callee = path.get("callee");
    if (callee.isIdentifier() && validateGlobalApi(callee, "createPortal")) {
      var args = path.get("arguments");
      if (args.length !== 1) {
        var missingArgs = args.length === 0;
        state.errors.push({
          message: "createPortal() requires exactly 1 argument, received ".concat(args.length),
          node: path.node,
          severity: missingArgs ? "error" : "warning"
        });
        if (missingArgs) {
          return null;
        }
      }
      var _children = [].concat(parseElement(args[0], state, app, options));
      return _children.map(function (child) {
        if (child === null) {
          return child;
        }
        if (child.type !== "component") {
          state.errors.push({
            message: "Invalid argument type for createPortal(), expected a component but got ".concat(child.type),
            node: args[0].node,
            severity: "error"
          });
          return child;
        }
        return _objectSpread(_objectSpread({}, child), {}, {
          component: _objectSpread(_objectSpread({}, child.component), {}, {
            portal: true
          })
        });
      });
    }
    if (isGeneralMemberExpression(callee)) {
      var property = callee.get("property");
      if (property.isIdentifier() && property.node.name === "map") {
        var _args = path.get("arguments");
        if (_args.length > 0) {
          var func = _args[0];
          if (func.isArrowFunctionExpression()) {
            var body = func.get("body");
            if (body.isExpression() && containsJsxNode(body.node)) {
              var object = callee.get("object");
              if (!validateEmbeddedExpression(object.node, state)) {
                return null;
              }
              var params = func.get("params");
              if (params.length > 2) {
                state.errors.push({
                  message: "Array map function with JSX elements must have at most two parameters, but got ".concat(params.length),
                  node: params[2].node,
                  severity: "error"
                });
                return null;
              }
              var invalidParam = params.find(function (p) {
                return !p.isIdentifier();
              });
              if (invalidParam) {
                state.errors.push({
                  message: "Array map function with JSX elements must have identifier parameters, but got ".concat(invalidParam.type),
                  node: invalidParam.node,
                  severity: "error"
                });
                return null;
              }
              var forEachOptions = _objectSpread(_objectSpread({}, options), {}, {
                forEachBinding: undefined
              });
              if (params.length > 0) {
                var _ref = params,
                  _ref2 = _slicedToArray(_ref, 2),
                  itemArg = _ref2[0],
                  indexArg = _ref2[1];
                forEachOptions.forEachBinding = {
                  item: itemArg.node,
                  index: indexArg === null || indexArg === void 0 ? void 0 : indexArg.node
                };
              }
              return {
                type: "component",
                component: {
                  name: "ForEach",
                  properties: {
                    dataSource: parsePropValue(object, state, app, _objectSpread(_objectSpread({}, options), {}, {
                      modifier: "="
                    }))
                  },
                  children: parseChildren(func.get("body"), state, app, forEachOptions)
                }
              };
            }
          }
        }
      }
    }
  } else if (path.isConditionalExpression()) {
    var test = path.get("test");
    var consequent = path.get("consequent");
    var alternate = path.get("alternate");
    if (containsJsxNode(consequent.node) || containsJsxNode(alternate.node)) {
      if (!validateEmbeddedExpression(test.node, state)) {
        return null;
      }
      return {
        type: "component",
        component: {
          name: "If",
          properties: {
            dataSource: parsePropValue(test, state, app, _objectSpread(_objectSpread({}, options), {}, {
              modifier: "="
            }))
          },
          children: [].concat(_toConsumableArray(parseChildren(consequent, state, app, options)), _toConsumableArray(parseChildren(alternate, state, app, options).map(function (component) {
            return _objectSpread(_objectSpread({}, component), {}, {
              slot: "else"
            });
          })))
        }
      };
    }
  } else if (path.isLogicalExpression()) {
    var left = path.get("left");
    var right = path.get("right");
    var operator = path.node.operator;
    if ((operator === "&&" || operator === "||") && containsJsxNode(right.node)) {
      if (!validateEmbeddedExpression(left.node, state)) {
        return null;
      }
      var _children2 = parseChildren(right, state, app, options);
      return {
        type: "component",
        component: {
          name: "If",
          properties: {
            dataSource: parsePropValue(left, state, app, _objectSpread(_objectSpread({}, options), {}, {
              modifier: "="
            }))
          },
          children: operator === "&&" ? _children2 : _children2.map(function (component) {
            return _objectSpread(_objectSpread({}, component), {}, {
              slot: "else"
            });
          })
        }
      };
    }
  }
  if (path.isExpression()) {
    if (validateEmbeddedExpression(path.node, state)) {
      return {
        type: "expression",
        expression: path
      };
    }
    return null;
  }
  state.errors.push({
    message: "Unsupported node type in JSX children: ".concat(path.type),
    node: path.node,
    severity: "error"
  });
  return null;
}
function containsJsxNode(expr) {
  var found = false;
  t.traverse(expr, {
    enter: function enter(node) {
      if (!found && (t.isJSXElement(node) || t.isJSXFragment(node))) {
        found = true;
      }
    }
  });
  return found;
}
//# sourceMappingURL=parseElement.js.map